import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import ModelService from '../../services/ModelService'

// GET /models returns { items, total, limit, skip } since AI4-207. The api and
// web services deploy from separate Cloud Build triggers, so during a rollout
// this code can briefly talk to an API that still returns a bare array —
// accept both rather than blanking the models list for that window.
const normalizeModelPage = (payload) => {
    if (Array.isArray(payload)) {
        return { items: payload, total: payload.length, limit: payload.length, skip: 0 };
    }
    return {
        items: payload?.items ?? [],
        total: payload?.total ?? 0,
        limit: payload?.limit ?? 0,
        skip: payload?.skip ?? 0,
    };
}

// Accepts { limit, skip, workspaceId }; omit them for the API's default page.
export const getModels = createAsyncThunk('models/getModels', async (paging) => {
    const page = await ModelService.getModels(paging);
    return normalizeModelPage(page)
})

export const createModels = createAsyncThunk('models/createModels', async (models, { rejectWithValue }) => {
    const result = await ModelService.putModels(models);
    // putModels uses parseJsonResponse which returns the error body (not an array) on
    // non-2xx responses instead of throwing, so we detect and reject explicitly here
    // to prevent the fulfilled reducer from spreading a non-iterable error object.
    if (!Array.isArray(result)) {
        return rejectWithValue(result);
    }
    return result;
})

export const deleteModel = createAsyncThunk('models/deleteModel', async (modelId) => {
    const obj = await ModelService.deleteModel(modelId);
    return obj?._id
})

export const bulkDeleteModels = createAsyncThunk('models/bulkDeleteModels', async (ids) => {
    return await ModelService.bulkDeleteModels(ids);
})

const initialState = {
    myModels: {
        value: [],
        // Paging metadata for the page currently in `value` (AI4-207).
        total: 0,
        limit: 0,
        skip: 0,
        loading: false,
        error: null
    },
    focusModel: {
        value: {},
        loading: false,
        error: null
    },
}

export const ModelSlice = createSlice({
    name: 'model',
    initialState,
    reducers: {
        selectFocusModel: (state, action) => {
            const modelId = action.payload
            state.focusModel.value = state.myModels.value.find(model => model.id === modelId)
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getModels.pending, (state) => {
                state.myModels.loading = true
                state.myModels.error = null
            })
            .addCase(getModels.fulfilled, (state, action) => {
                state.myModels.loading = false
                state.myModels.value = action.payload.items
                state.myModels.total = action.payload.total
                state.myModels.limit = action.payload.limit
                state.myModels.skip = action.payload.skip
            })
            .addCase(getModels.rejected, (state, action) => {
                state.myModels.loading = false
                state.myModels.error = action.error.message
            })


        builder
            .addCase(createModels.pending, (state) => {
                state.myModels.loading = true
                state.myModels.error = null
            })
            .addCase(createModels.fulfilled, (state, action) => {
                state.myModels.loading = false
                state.myModels.value = [...state.myModels.value, ...action.payload]
                // Keep `total` in step with the local edit so the pager footer
                // doesn't disagree with the rows on screen until the next fetch.
                state.myModels.total += action.payload.length
            })
            .addCase(createModels.rejected, (state, action) => {
                state.myModels.loading = false
                state.myModels.error = action.error.message
            })


        builder
            .addCase(bulkDeleteModels.fulfilled, (state, action) => {
                const deletedIds = new Set(
                    (action.payload?.results || [])
                        .filter((r) => r.status === 'deleted')
                        .map((r) => r.id),
                );
                if (deletedIds.size > 0) {
                    const before = state.myModels.value.length;
                    state.myModels.value = state.myModels.value.filter(
                        (m) => !deletedIds.has(m._id),
                    );
                    state.myModels.total = Math.max(
                        state.myModels.total - (before - state.myModels.value.length),
                        0,
                    );
                }
            })
            .addCase(bulkDeleteModels.rejected, (state, action) => {
                state.myModels.error = action.error.message;
            })

        builder
            .addCase(deleteModel.fulfilled, (state, action) => {
                state.myModels.loading = false
                console.log('models', state.myModels.value?.map(model => model._id))
                console.log('deleteModel.fulfilled', action.payload)
                const before = state.myModels.value.length
                state.myModels.value = state.myModels.value.filter(model => {
                    console.log(model._id, 'vs', action.payload, model._id == action.payload)
                    return model._id != action.payload
                })
                state.myModels.total = Math.max(
                    state.myModels.total - (before - state.myModels.value.length),
                    0,
                )
            })
            .addCase(deleteModel.rejected, (state, action) => {
                state.myModels.loading = false
                state.myModels.error = action.error.message
            })
    },
})

// Action creators are generated for each case reducer function
export const { selectFocusModel } = ModelSlice.actions
export const selectModels = (state) => state.model.myModels.value
export const selectLoading = (state) => state.model.myModels.loading
export const selectError = (state) => state.model.myModels.error
export default ModelSlice.reducer